/**
 * Fixes corrupted data where null/undefined values were stored as strings.
 * Converts string 'null' back to actual null and string 'undefined' back to actual undefined.
 * Recursively processes nested objects and arrays.
 * 
 * @param {any} obj - The object to sterilize
 * @returns {any} The sterilized object with proper null/undefined values
 * @example
 * const corrupted = { 
 *   name: 'Equipment A', 
 *   serialNumber: 'null',
 *   location: 'undefined',
 *   nested: { value: 'null' }
 * };
 * const fixed = sterilizeObject(corrupted);
 * // Result: { 
 * //   name: 'Equipment A', 
 * //   serialNumber: null,
 * //   location: undefined,
 * //   nested: { value: null }
 * // }
 */
export const sterilizeObject = <T = any>(obj: T): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    if (obj === 'null') return null as T;
    if (obj === 'undefined') return undefined as T;
    return obj;
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sterilizeObject(item)) as T;
  }

  const sanitizedObj = {} as T;
  
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      
      if (value === 'null') {
        (sanitizedObj as any)[key] = null;
      } else if (value === 'undefined') {
        (sanitizedObj as any)[key] = undefined;
      } else if (typeof value === 'object' && value !== null) {
        // Recursively handle nested objects and arrays
        (sanitizedObj as any)[key] = sterilizeObject(value);
      } else {
        (sanitizedObj as any)[key] = value;
      }
    }
  }
  
  return sanitizedObj;
};

/**
 * Applies sterilizeObject to an array of objects.
 * Fixes corrupted data in multiple objects where null/undefined values were stored as strings.
 * 
 * @param {any[]} objList - Array of objects to sterilize
 * @returns {any[]} Array of sterilized objects with proper null/undefined values
 * @throws {Error} When input is not an array
 * @example
 * const corruptedList = [
 *   { name: 'Item 1', value: 'null' },
 *   { name: 'Item 2', value: 'undefined' }
 * ];
 * const fixedList = sterilizeObjectList(corruptedList);
 * // Result: [
 * //   { name: 'Item 1', value: null },
 * //   { name: 'Item 2', value: undefined }
 * // ]
 */
export const sterilizeObjectList = <T = any>(objList: T[]): T[] => {
  if (!Array.isArray(objList)) {
    throw new Error('Input must be an array. Received: ' + typeof objList);
  }
  
  return objList.map(obj => sterilizeObject(obj));
};